import React from 'react';
import { 
  MessageSquare,
  Calendar,
  Users,
  Target,
  CreditCard,
  Bot,
  Megaphone,
  Zap,
  Globe,
  UserCircle,
  FolderOpen,
  Star,
  BarChart3,
  LayoutGrid,
  Settings
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

interface MockupSidebarProps {
  activeTab: number;
  animationDelay?: number;
  cursorPosition?: { x: number; y: number } | null;
}

// Main menu items
const mainMenuItems = [
  { icon: MessageSquare, label: 'Conversations', tabIndex: 5 },
  { icon: Calendar, label: 'Calendars', tabIndex: 2 },
  { icon: Users, label: 'Contacts' },
  { icon: Target, label: 'Opportunities' },
  { icon: CreditCard, label: 'Payments' },
];

// Secondary menu items (after separator)
const secondaryMenuItems = [
  { icon: Bot, label: 'AI Agents', tabIndex: 1 },
  { icon: Megaphone, label: 'Marketing', tabIndex: 3 },
  { icon: Zap, label: 'Automation', tabIndex: 1 },
  { icon: Globe, label: 'Sites', tabIndex: 4 },
  { icon: UserCircle, label: 'Memberships' },
  { icon: FolderOpen, label: 'Media Storage' },
  { icon: Star, label: 'Reputation' },
  { icon: BarChart3, label: 'Reporting', tabIndex: 0 },
  { icon: LayoutGrid, label: 'App Marketplace' },
];

const MockupSidebar: React.FC<MockupSidebarProps> = ({ activeTab, animationDelay = 0 }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Map activeTab to highlight specific menu items
  const getActiveState = (item: { tabIndex?: number }) => {
    if (item.tabIndex === undefined) return false;
    return item.tabIndex === activeTab;
  };

  const renderMenuItem = (
    item: { icon: React.ElementType; label: string; tabIndex?: number },
    index: number,
    baseDelay: number,
    isActive: boolean
  ) => {
    const Icon = item.icon;
    
    return (
      <div
        key={index}
        className="group flex items-center gap-1 lg:gap-2 px-1.5 lg:px-2.5 py-1 lg:py-1.5 rounded-md cursor-pointer transition-all duration-200 relative overflow-hidden hover:scale-[1.02]"
        style={{
          backgroundColor: isActive 
            ? (isDark ? '#2A2A2A' : '#EBEBEB')
            : 'transparent',
          animation: `slideInFromLeft 0.3s ease-out ${baseDelay + (index * 40)}ms both`
        }}
      >
        {/* Hover background */}
        <div 
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-md"
          style={{ 
            backgroundColor: isDark ? '#252525' : '#F0F0F0',
            display: isActive ? 'none' : 'block'
          }}
        />
        
        <Icon 
          className="w-3 h-3 lg:w-3.5 lg:h-3.5 flex-shrink-0 relative z-10 transition-colors duration-200 group-hover:text-blue-500" 
          style={{ 
            color: isActive 
              ? (isDark ? '#FFFFFF' : '#111111')
              : (isDark ? '#888888' : '#666666')
          }} 
        />
        {/* Hide labels on mobile, show on lg */}
        <span 
          className="hidden lg:block text-[10px] font-medium truncate relative z-10 transition-colors duration-200"
          style={{ 
            color: isActive 
              ? (isDark ? '#FFFFFF' : '#111111')
              : (isDark ? '#888888' : '#666666')
          }}
        >
          {item.label}
        </span>
        {isActive && (
          <div 
            className="ml-auto w-0.5 lg:w-1 h-2.5 lg:h-3.5 rounded-full relative z-10"
            style={{ 
              backgroundColor: isDark ? '#FFFFFF' : '#111111',
              animation: 'scaleIn 0.2s ease-out'
            }}
          />
        )}
      </div>
    );
  };

  return (
    <div 
      className="flex flex-col py-1.5 lg:py-2 border-r h-full"
      style={{
        width: 'clamp(40px, 10vw, 130px)',
        minWidth: '40px',
        borderColor: isDark ? '#333333' : '#E5E5E5',
        backgroundColor: isDark ? '#1A1A1A' : '#FAFAFA',
        animation: `slideInFromLeft 0.4s ease-out ${animationDelay}ms both`
      }}
    >
      <nav className="flex-1 px-1 lg:px-1.5 overflow-hidden">
        {/* Main menu items */}
        <div className="space-y-0.5 mb-1.5 lg:mb-2">
          {mainMenuItems.map((item, index) => 
            renderMenuItem(item, index, animationDelay + 100, getActiveState(item))
          )}
        </div>

        {/* Separator */}
        <div 
          className="mx-1 lg:mx-2 my-1.5 lg:my-2 border-t"
          style={{ 
            borderColor: isDark ? '#333333' : '#E5E5E5',
            animation: `fadeIn 0.3s ease-out ${animationDelay + 300}ms both`
          }}
        />

        {/* Secondary menu items */}
        <div className="space-y-0.5">
          {secondaryMenuItems.map((item, index) => 
            renderMenuItem(item, index, animationDelay + 350, getActiveState(item))
          )}
        </div>
      </nav>

      {/* Settings at bottom */}
      <div 
        className="px-1 lg:px-1.5 pt-1.5 lg:pt-2 mt-auto border-t"
        style={{ 
          borderColor: isDark ? '#333333' : '#E5E5E5',
          animation: `fadeIn 0.4s ease-out ${animationDelay + 700}ms both`
        }}
      >
        <div
          className="group flex items-center gap-1 lg:gap-2 px-1.5 lg:px-2.5 py-1 lg:py-1.5 rounded-md cursor-pointer transition-all duration-200 hover:scale-[1.02] relative"
          style={{ backgroundColor: 'transparent' }}
        >
          <div 
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-md"
            style={{ backgroundColor: isDark ? '#252525' : '#F0F0F0' }}
          />
          <Settings className="w-3 h-3 lg:w-3.5 lg:h-3.5 relative z-10 transition-colors duration-200 group-hover:text-blue-500" style={{ color: isDark ? '#888888' : '#666666' }} />
          <span 
            className="hidden lg:block text-[10px] font-medium relative z-10"
            style={{ color: isDark ? '#888888' : '#666666' }}
          >
            Settings
          </span>
        </div>
      </div>
    </div>
  );
};

export default MockupSidebar;
